import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, TreePine, Building, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export const AboutPage = () => {
  const stats = [
    { value: '1,200+', label: 'Tech Companies' },
    { value: '70,000+', label: 'Tech Workers' },
    { value: '$2.8B', label: 'VC Investment (2023)' },
    { value: '300+', label: 'Annual Tech Events' },
  ];

  const timeline = [
    { year: '1970s', title: 'Portland Tech Origins', description: 'Tektronix and Intel establish semiconductor manufacturing in the Portland area.' },
    { year: '1980s', title: 'Hardware Boom', description: 'Major growth in chip manufacturing and hardware engineering, establishing Portland as a tech hub.' },
    { year: '1990s', title: 'Open Source Rise', description: 'Portland becomes a center for open source development, home to early Mozilla contributors.' },
    { year: '2000s', title: 'Software Evolution', description: 'Shift toward software, with companies like Jive Software and Puppet leading the way.' },
    { year: '2010s', title: 'Startup Renaissance', description: 'Accelerators, coworking spaces, and a thriving startup ecosystem emerge.' },
    { year: '2020s', title: 'AI & Innovation', description: 'Portland leads in AI, sustainability tech, and remote-first companies.' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="border-b border-border">
        <div className="container-wide py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-label text-muted-foreground mb-4 block">About</span>
              <h1 className="text-display text-4xl lg:text-6xl mb-6">
                Portland Tech<br />Directory
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                Portland's technology ecosystem has grown from hardware manufacturing 
                roots into a diverse, innovative community spanning AI, robotics, 
                software, and sustainable technology.
              </p>
              <div className="flex gap-4">
                <Link to="/communities">
                  <Button size="lg">
                    Explore Communities
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1567955567057-5b7a3c5aff7d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Portland skyline"
                className="rounded-sm grayscale-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-muted/30">
        <div className="container-wide py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl lg:text-5xl font-extrabold tracking-tighter mb-2">
                  {stat.value}
                </div>
                <div className="text-label text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Portland */}
      <section className="border-b border-border">
        <div className="container-wide py-16">
          <div className="mb-12">
            <span className="text-label text-muted-foreground mb-4 block">Why Portland</span>
            <h2 className="text-heading text-3xl">What Makes Portland Unique</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="card-hover">
              <CardContent className="p-6">
                <TreePine className="w-8 h-8 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Quality of Life</h3>
                <p className="text-sm text-muted-foreground">
                  Access to nature, lower cost of living than SF/Seattle, 
                  and a culture that values work-life balance.
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <Building className="w-8 h-8 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Sales Tax</h3>
                <p className="text-sm text-muted-foreground">
                  Oregon's tax structure makes it attractive for both 
                  businesses and individuals in the tech industry.
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <Users className="w-8 h-8 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Collaborative Culture</h3>
                <p className="text-sm text-muted-foreground">
                  A tight-knit community where founders, engineers, and 
                  investors support each other's success.
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="p-6">
                <TrendingUp className="w-8 h-8 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Growing Fast</h3>
                <p className="text-sm text-muted-foreground">
                  Remote work has accelerated Portland's tech growth, 
                  attracting talent from across the country.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="border-b border-border">
        <div className="container-wide py-16">
          <div className="mb-12">
            <span className="text-label text-muted-foreground mb-4 block">History</span>
            <h2 className="text-heading text-3xl">Portland Tech Timeline</h2>
          </div>
          <div className="space-y-0 border-l border-border ml-4 lg:ml-8">
            {timeline.map((item, index) => (
              <div 
                key={index}
                className="relative pl-8 pb-12 last:pb-0"
              >
                <div className="absolute left-0 top-0 w-2 h-2 bg-primary rounded-full -translate-x-1/2" />
                <div className="text-label text-muted-foreground mb-2">{item.year}</div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-muted/30">
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-heading text-3xl mb-4">Join the Community</h2>
            <p className="text-muted-foreground mb-8">
              Whether you're a founder, developer, investor, or just curious about 
              Portland's tech scene, there's a place for you here.
            </p>
            <div className="flex justify-center gap-4">
              <Link to="/get-involved">
                <Button size="lg">
                  Get Involved
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/events">
                <Button variant="outline" size="lg">
                  Browse Events
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
